import {
  ApiError,
  deliveryAction,
  getQueue,
  getSentOverview,
  retryFailed,
  type DeliveryStats,
  type QueueSnapshot,
  type SentOverview,
  type StatsWindow,
} from "@/api";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import {
  DeliveryStatsBar,
  DeliveryStatsLegend,
  failedCount,
  formatPercent,
} from "@/components/ui/DeliveryStatsBar";
import { cn } from "@/lib/cn";
import { formatDateTime } from "@/lib/format";
import {
  DELIVERY_STAT_LABEL,
  EVENT_TYPE_LABEL,
  MESSAGE_PHASE_LABEL,
  STAT_WINDOWS,
  eventStatusLabel,
  statusTone,
  typeTone,
} from "@/lib/labels";
import { useCallback, useEffect, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";

const STATE_TONE: Record<string, "muted" | "accent" | "warn" | "danger" | "success"> = {
  queued: "accent",
  deferred: "warn",
  submitting: "warn",
  unknown: "danger",
  rejected: "danger",
  failed: "danger",
  submitted: "success",
  cancelled: "muted",
};

const OPEN_ORDER = [
  "queued",
  "deferred_scheduled",
  "deferred_rate_limit",
  "deferred_retry",
  "deferred_smtp",
  "submitting",
  "unknown",
] as const;

const fmt = (n: number) => n.toLocaleString("de-DE");

function Stage({
  label,
  value,
  hint,
  tone,
}: {
  label: string;
  value: number;
  hint: string;
  tone?: "good";
}) {
  return (
    <div className="flex min-w-0 flex-col gap-0.5 rounded-md border border-hairline px-3 py-2">
      <span className="text-[11px] font-medium uppercase tracking-wide text-muted">{label}</span>
      <span className={cn("text-xl font-semibold tabular-nums", tone === "good" && "text-green")}>
        {fmt(value)}
      </span>
      <span className="text-[11px] text-muted">{hint}</span>
    </div>
  );
}

function Branch({ dot, value, children }: { dot: string; value: number; children: ReactNode }) {
  return (
    <div className={cn("flex items-baseline gap-1.5 text-xs", value === 0 && "text-muted")}>
      <span className={cn("inline-block h-2 w-2 shrink-0 rounded-full", dot)} />
      <strong className="tabular-nums">{fmt(value)}</strong> {children}
    </div>
  );
}

function Funnel({ mails, stats }: { mails: number; stats: DeliveryStats }) {
  const arrow = "hidden text-muted sm:flex sm:items-center";
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_auto_1fr_auto_1fr_auto_1fr]">
        <Stage label="Mails" value={mails} hint="versendet" />
        <span className={arrow}>→</span>
        <Stage label="Empfänger" value={stats.total} hint="Kopien erzeugt" />
        <span className={arrow}>→</span>
        <Stage
          label="Angenommen"
          value={stats.submitted}
          hint={`${formatPercent(stats.submitted, stats.total)} vom Mailserver`}
        />
        <span className={arrow}>→</span>
        <Stage
          label="Zugestellt"
          value={stats.delivered}
          hint={`${formatPercent(stats.delivered, stats.total)} ohne Rückläufer`}
          tone="good"
        />
      </div>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_auto_1fr_auto_1fr_auto_1fr]">
        <span className="hidden sm:block" />
        <span className="hidden sm:block sm:w-3" />
        <div className="ml-3 space-y-1 border-l-2 border-hairline pl-3">
          <Branch dot="bg-danger" value={stats.rejected}>abgelehnt (5xx)</Branch>
          <Branch dot="bg-danger" value={stats.failed}>fehlgeschlagen</Branch>
          <Branch dot="bg-danger opacity-40" value={stats.unknown}>unbekannt</Branch>
          <Branch dot="bg-accent" value={stats.queued}>noch offen</Branch>
        </div>
        <span className="hidden sm:block sm:w-3" />
        <div className="ml-3 space-y-1 border-l-2 border-hairline pl-3">
          <Branch dot="bg-amber" value={stats.bounced}>Rückläufer</Branch>
          <p className="text-[11px] text-muted">später als unzustellbar gemeldet</p>
        </div>
      </div>
      <p className="text-[11px] text-muted">
        „Angenommen“ heißt: unser Mailserver hat die Kopie übernommen. Zugestellt ist sie erst, wenn
        kein Rückläufer kommt. Rückläufer zählen bei ihrer Mail, auch wenn sie Tage später eintreffen.
      </p>
    </div>
  );
}

function OpenStrip({ open }: { open: Record<string, number> }) {
  const keys = OPEN_ORDER.filter((key) => (open[key] ?? 0) > 0);
  if (keys.length === 0) return null;
  const total = keys.reduce((sum, key) => sum + open[key], 0);
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-hairline border-l-accent border-l-[3px] px-3 py-2 text-xs">
      <a href="#offen" className="hover:underline">
        <strong>Gerade in Arbeit:</strong> <span className="tabular-nums">{fmt(total)}</span>{" "}
        {total === 1 ? "Kopie" : "Kopien"}
      </a>
      <div className="flex flex-wrap gap-1.5">
        {keys.map((key) => (
          <span key={key} className="rounded-md border border-hairline bg-surface-subtle px-2 py-0.5">
            {DELIVERY_STAT_LABEL[key] ?? key}{" "}
            <strong className="tabular-nums">{fmt(open[key])}</strong>
          </span>
        ))}
      </div>
    </div>
  );
}

function Count({ value, tone }: { value: number; tone: string }) {
  return <span className={cn("tabular-nums", value > 0 ? tone : "text-muted/60")}>{fmt(value)}</span>;
}

const ROW_GRID =
  "grid grid-cols-[7.5rem_minmax(0,1fr)_4.5rem_4.5rem_4rem_4.5rem_8rem] items-center gap-x-3";

function EventGroup({
  event,
  expanded,
  onToggle,
}: {
  event: SentOverview["events"][number];
  expanded: boolean;
  onToggle: () => void;
}) {
  const failed = failedCount(event.stats);
  const mails = event.messages.length;
  return (
    <div className="overflow-hidden rounded-md border border-hairline">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        className="flex w-full flex-wrap items-center justify-between gap-x-4 gap-y-1 bg-surface-subtle px-3 py-2 text-left hover:bg-surface-subtle/70"
      >
        <span className="flex min-w-0 flex-wrap items-center gap-2">
          <span className={cn("text-[10px] text-muted transition-transform motion-reduce:transition-none", expanded && "rotate-90")}>
            ▶
          </span>
          <span className="truncate text-sm font-medium">{event.title || "Ohne Titel"}</span>
          <Badge tone={typeTone(event.type)}>{EVENT_TYPE_LABEL[event.type]}</Badge>
          <Badge tone={statusTone(event.status)}>{eventStatusLabel(event)}</Badge>
        </span>
        <span className="flex items-center gap-3 text-xs">
          <span className="tabular-nums text-muted">
            {mails} {mails === 1 ? "Mail" : "Mails"} · {fmt(event.stats.total)} Kopien
          </span>
          {failed > 0 ? <span className="tabular-nums text-danger">{fmt(failed)} Fehler</span> : null}
          {event.stats.bounced > 0 ? (
            <span className="tabular-nums text-amber">{fmt(event.stats.bounced)} Rückläufer</span>
          ) : null}
          <DeliveryStatsBar stats={event.stats} className="w-24" />
        </span>
      </button>
      {expanded ? (
        <div className="overflow-x-auto">
          <div className="min-w-[44rem] text-xs">
            <div className={cn(ROW_GRID, "border-t border-hairline px-3 py-1.5 text-[11px] text-muted")}>
              <span>Gesendet</span>
              <span>Mail</span>
              <span className="text-right">Empfänger</span>
              <span className="text-right">Zugestellt</span>
              <span className="text-right">Fehler</span>
              <span className="text-right">Rückläufer</span>
              <span />
            </div>
            {event.messages.map((message) => {
              const s = message.stats;
              return (
                <Link
                  key={message.id}
                  to={`/queue/messages/${message.id}`}
                  className={cn(ROW_GRID, "border-t border-hairline px-3 py-2 hover:bg-surface-subtle")}
                >
                  <span className="tabular-nums text-muted">{formatDateTime(message.sent_at)}</span>
                  <span className="min-w-0">
                    <span className="block font-medium">
                      {MESSAGE_PHASE_LABEL[message.phase] ?? message.phase}
                    </span>
                    <span className="block truncate text-muted">{message.subject || "Ohne Betreff"}</span>
                  </span>
                  <span className="text-right tabular-nums">{fmt(s.total)}</span>
                  <span className="text-right">
                    <Count value={s.delivered} tone="text-green" />
                  </span>
                  <span className="text-right">
                    <Count value={failedCount(s)} tone="text-danger" />
                  </span>
                  <span className="text-right">
                    <Count value={s.bounced} tone="text-amber" />
                  </span>
                  <span className="space-y-1">
                    <DeliveryStatsBar stats={s} />
                    <span className="block text-[11px] text-muted">
                      {s.queued > 0
                        ? `${fmt(s.queued)} noch offen`
                        : `${formatPercent(s.delivered, s.total)} zugestellt`}
                    </span>
                  </span>
                </Link>
              );
            })}
            <div className="border-t border-hairline px-3 py-1.5 text-right">
              <Link to={`/events/${event.id}`} className="text-[11px] text-accent hover:underline">
                Zum Vorgang
              </Link>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function needsAttention(event: SentOverview["events"][number]): boolean {
  return failedCount(event.stats) > 0 || event.stats.queued > 0 || event.stats.bounced > 0;
}

export function QueuePage() {
  const [queue, setQueue] = useState<QueueSnapshot | null>(null);
  const [overview, setOverview] = useState<SentOverview | null>(null);
  const [windowId, setWindowId] = useState<StatsWindow>("7d");
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const refreshQueue = useCallback(async () => {
    try {
      setQueue(await getQueue());
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Queue konnte nicht geladen werden.");
    }
  }, []);

  const refreshOverview = useCallback(async () => {
    try {
      setOverview(await getSentOverview(windowId));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Versand konnte nicht geladen werden.");
    }
  }, [windowId]);

  const refresh = useCallback(
    () => Promise.all([refreshQueue(), refreshOverview()]),
    [refreshQueue, refreshOverview],
  );

  useEffect(() => {
    void refreshQueue();
    const timer = window.setInterval(() => void refreshQueue(), 2000);
    return () => window.clearInterval(timer);
  }, [refreshQueue]);

  useEffect(() => {
    void refreshOverview();
    const timer = window.setInterval(() => void refreshOverview(), 5000);
    return () => window.clearInterval(timer);
  }, [refreshOverview]);

  async function act(id: string, action: "retry" | "ack" | "cancel") {
    setBusy(id);
    try {
      await deliveryAction(id, action);
      await refresh();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Aktion fehlgeschlagen.");
    } finally {
      setBusy(null);
    }
  }

  async function requeue(state: "failed" | "rejected") {
    setBusy(`bulk-${state}`);
    try {
      const { count } = await retryFailed([state]);
      setNotice(`${count} ${count === 1 ? "Kopie" : "Kopien"} wieder in der Queue.`);
      await refresh();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Erneut senden fehlgeschlagen.");
    } finally {
      setBusy(null);
    }
  }

  const failedTotal = queue?.counts.failed ?? 0;
  const rejectedTotal = queue?.counts.rejected ?? 0;

  function isExpanded(event: SentOverview["events"][number], index: number): boolean {
    return expanded[event.id] ?? (index < 3 || needsAttention(event));
  }

  return (
    <div className="mx-auto w-full max-w-5xl px-4 py-4">
      <div className="mb-4">
        <h1 className="text-base font-semibold">Queue</h1>
      </div>
      {error ? <p className="mb-3 text-sm text-danger">{error}</p> : null}

      {overview ? (
        <section className="mb-5 space-y-4 rounded-lg border border-hairline bg-surface p-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-sm font-semibold">Versand</h2>
            <div className="flex gap-1">
              {STAT_WINDOWS.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => setWindowId(item.id)}
                  className={cn(
                    "rounded-md px-2 py-1 text-xs",
                    windowId === item.id
                      ? "bg-accent-dim text-accent"
                      : "text-muted hover:bg-surface-subtle hover:text-ink",
                  )}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>
          <Funnel mails={overview.mails} stats={overview.stats} />
          <OpenStrip open={overview.open} />
          <div className="space-y-2">
            <p className="text-[11px] font-medium uppercase tracking-wide text-muted">
              {windowId === "all" ? "Vorgänge mit Versand" : "Vorgänge mit Versand im Zeitraum"}
            </p>
            {overview.events.length === 0 ? (
              <p className="py-3 text-center text-xs text-muted">Im Zeitraum wurde keine Mail versendet.</p>
            ) : (
              overview.events.map((event, index) => (
                <EventGroup
                  key={event.id}
                  event={event}
                  expanded={isExpanded(event, index)}
                  onToggle={() =>
                    setExpanded((prev) => ({ ...prev, [event.id]: !isExpanded(event, index) }))
                  }
                />
              ))
            )}
            {overview.events_total > overview.events.length ? (
              <p className="text-[11px] text-muted">
                Die neuesten {overview.events.length} von {overview.events_total} Vorgängen. Die Zahlen oben
                zählen alle.
              </p>
            ) : null}
            <DeliveryStatsLegend />
          </div>
        </section>
      ) : (
        <div className="mb-5 h-28 animate-pulse rounded-lg bg-surface-subtle" />
      )}

      {failedTotal > 0 || rejectedTotal > 0 ? (
        <section className="mb-4 flex flex-wrap items-center gap-2 rounded-lg border border-hairline bg-surface p-3 text-xs">
          <p className="mr-auto min-w-0 text-muted">
            Insgesamt {failedTotal > 0 ? `${failedTotal} fehlgeschlagen` : null}
            {failedTotal > 0 && rejectedTotal > 0 ? " · " : null}
            {rejectedTotal > 0 ? `${rejectedTotal} vom Mailserver abgelehnt (5xx)` : null}
            <span className="block text-[11px]">
              Über alle Mails, auch ältere. Einzelne Mails lassen sich in ihrer Detailansicht erneut senden.
            </span>
          </p>
          {failedTotal > 0 ? (
            <Button size="sm" disabled={busy !== null} onClick={() => void requeue("failed")}>
              Fehlgeschlagene erneut senden
            </Button>
          ) : null}
          {rejectedTotal > 0 ? (
            <Button size="sm" variant="ghost" disabled={busy !== null} onClick={() => void requeue("rejected")}>
              Abgelehnte erneut senden
            </Button>
          ) : null}
        </section>
      ) : null}
      {notice ? <p className="mb-3 text-xs text-muted">{notice}</p> : null}

      <h2 id="offen" className="mb-2 text-sm font-semibold">
        Offene Kopien
      </h2>
      {!queue ? (
        <p className="text-sm text-muted">Laden…</p>
      ) : queue.items.length === 0 ? (
        <p className="text-sm text-muted">Nichts offen. Geplante und wartende Kopien erscheinen hier.</p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-hairline">
          <table className="w-full text-left text-xs">
            <thead className="bg-surface-subtle text-muted">
              <tr>
                <th className="px-3 py-2 font-medium">Empfänger</th>
                <th className="px-3 py-2 font-medium">Mail</th>
                <th className="px-3 py-2 font-medium">Status</th>
                <th className="px-3 py-2 font-medium">Nächster Versuch</th>
                <th className="px-3 py-2 font-medium" />
              </tr>
            </thead>
            <tbody>
              {queue.items.map((item) => (
                <tr key={item.id} className="border-t border-hairline">
                  <td className="px-3 py-2 font-mono">
                    {item.address}
                    <div className="text-[10px] text-muted">{item.domain}</div>
                  </td>
                  <td className="px-3 py-2">
                    <Link to={`/queue/messages/${item.message_id}`} className="text-accent hover:underline">
                      {item.event_title}
                    </Link>
                  </td>
                  <td className="px-3 py-2">
                    <Badge tone={STATE_TONE[item.state] ?? "muted"}>{item.state}</Badge>
                    {item.defer_reason ? (
                      <span className="ml-1 text-muted">{item.defer_reason}</span>
                    ) : null}
                    {item.last_error ? (
                      <div className="mt-0.5 max-w-xs truncate text-[10px] text-danger">{item.last_error}</div>
                    ) : null}
                  </td>
                  <td className="px-3 py-2 text-muted">
                    {item.next_attempt_at ? formatDateTime(item.next_attempt_at) : "—"}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {item.state === "unknown" ? (
                      <span className="flex justify-end gap-1">
                        <Button size="sm" disabled={busy === item.id} onClick={() => void act(item.id, "retry")}>
                          Erneut
                        </Button>
                        <Button size="sm" variant="ghost" disabled={busy === item.id} onClick={() => void act(item.id, "ack")}>
                          War draußen
                        </Button>
                      </span>
                    ) : null}
                    {item.state === "queued" || item.state === "deferred" ? (
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={busy === item.id}
                        onClick={() => void act(item.id, "cancel")}
                      >
                        Cancel
                      </Button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
